import React, { useRef, useEffect, useId, forwardRef, useImperativeHandle, useMemo } from 'react'
import { MessageSquare, SendHorizonal, ImagePlus, Paperclip, X, Check, Rocket, FileText } from 'lucide-react'
import styles from './ConversationInput.module.scss'
import draftPublishStyles from '../DraftPublishButton.module.scss'
import {
  composerUsesMediaOnlyIcon,
  getComposerFileAccept
} from '../../../../../utils/conversationalMode/composerAttachmentAccept.ts'
import type { PostMediaConfirmRoute } from '../../../../../utils/conversationalMode/composerDocumentChoice.ts'

const isEditableFocused = (): boolean => {
  const el = document.activeElement
  if (!el) return false
  const tag = el.tagName.toLowerCase()
  if (tag === 'input' || tag === 'textarea') return true
  if (el.getAttribute('contenteditable') === 'true') return true
  return false
}

const isPrintableKey = (e: KeyboardEvent): boolean => {
  if (e.ctrlKey || e.metaKey || e.altKey) return false
  if (!e.key || e.key.length !== 1) return false
  const special = ['Enter', 'Tab', 'Escape', 'Backspace', 'Delete', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']
  return !special.includes(e.key)
}

const normalizeFileNameForCompare = (s: string) =>
  s
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const formatCompactFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 0) return ''
  if (bytes < 1024) return `${Math.round(bytes)} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  const mb = bytes / (1024 * 1024)
  return mb >= 10 ? `${Math.round(mb)} MB` : `${Math.round(mb * 10) / 10} MB`
}

export type ConversationInputHandle = {
  openAttachmentPicker: () => void
}

interface ConversationInputProps {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  onKeyPress: (e: React.KeyboardEvent) => void
  disabled?: boolean
  placeholder?: string
  showImageUpload?: boolean
  allowVideo?: boolean
  allowDocuments?: boolean
  highlightProjectAutofillAttachment?: boolean
  hasPendingImage?: boolean
  imageUploadAcknowledged?: boolean
  onImageSelect?: (file: File) => void
  onImageClear?: () => void
  documentIntentChoicePending?: boolean
  onConfirmDocumentAsProjectPreload?: () => void | Promise<void>
  postMediaIntentChoicePending?: boolean
  onConfirmPostMediaRoute?: (route: PostMediaConfirmRoute) => void | Promise<void>
  pendingImagePreview?: string | null
  pendingAttachmentMime?: string | null
  pendingAttachmentFileName?: string | null
  pendingAttachmentSizeBytes?: number | null
  lastAnalyzedDocumentationFileName?: string | null
  readyToExecute?: boolean
  onConfirmExecution?: () => void
  showDraftPublish?: boolean
  draftPublishing?: boolean
  onPublishDraft?: () => void
  onOpenHistory?: () => void
  historyCount?: number
  variant?: 'composer' | 'welcome'
  onInputFocus?: () => void
  onInputBlur?: () => void
  onWelcomeReset?: () => void
  welcomeTextareaClassName?: string
  welcomeSendClassName?: string
  welcomeActionsClassName?: string
  showProjectAutofillSendHint?: boolean
  showPostMediaSendHint?: boolean
}

const ConversationInput = forwardRef<ConversationInputHandle, ConversationInputProps>(
  (
    {
      value,
      onChange,
      onSend,
      onKeyPress,
      disabled = false,
      placeholder = 'Escribí tu mensaje...',
      showImageUpload = false,
      allowVideo = false,
      allowDocuments = false,
      highlightProjectAutofillAttachment = false,
      hasPendingImage = false,
      imageUploadAcknowledged = false,
      onImageSelect,
      onImageClear,
      documentIntentChoicePending = false,
      onConfirmDocumentAsProjectPreload,
      postMediaIntentChoicePending = false,
      onConfirmPostMediaRoute,
      pendingImagePreview = null,
      pendingAttachmentMime = null,
      pendingAttachmentFileName = null,
      pendingAttachmentSizeBytes = null,
      lastAnalyzedDocumentationFileName = null,
      readyToExecute = false,
      onConfirmExecution,
      showDraftPublish = false,
      draftPublishing = false,
      onPublishDraft,
      onOpenHistory,
      historyCount,
      variant = 'composer',
      onInputFocus,
      onInputBlur,
      onWelcomeReset,
      welcomeTextareaClassName,
      welcomeSendClassName,
      welcomeActionsClassName,
      showProjectAutofillSendHint = false,
      showPostMediaSendHint = false
    },
    ref
  ) => {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const valueRef = useRef(value)
    const onChangeRef = useRef(onChange)
    const fileInputId = useId()
    valueRef.current = value
    onChangeRef.current = onChange

    useImperativeHandle(
      ref,
      () => ({
        openAttachmentPicker: () => {
          if (!showImageUpload) return
          fileInputRef.current?.click()
        }
      }),
      [showImageUpload]
    )

    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (disabled || !isPrintableKey(e)) return
        if (isEditableFocused()) return

        e.preventDefault()
        textareaRef.current?.focus()
        onChangeRef.current(valueRef.current + e.key)
      }

      document.addEventListener('keydown', handleKeyDown, { capture: true })
      return () => document.removeEventListener('keydown', handleKeyDown, { capture: true })
    }, [disabled])

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file && onImageSelect) {
        onImageSelect(file)
      }
      e.target.value = ''
    }

    const handleDragOver = (e: React.DragEvent) => {
      if (!showImageUpload || disabled) return
      e.preventDefault()
      e.stopPropagation()
    }

    const handleDrop = (e: React.DragEvent) => {
      if (!showImageUpload || disabled) return
      e.preventDefault()
      e.stopPropagation()
      const file = e.dataTransfer.files?.[0]
      if (file && onImageSelect) {
        void onImageSelect(file)
      }
    }

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const files = e.clipboardData?.files
      if (!files?.length || !onImageSelect) return
      const file = files[0]
      if (!file) return
      e.preventDefault()
      void onImageSelect(file)
    }

    const canSend =
      (value.trim() || hasPendingImage) &&
      !documentIntentChoicePending &&
      !postMediaIntentChoicePending

    const handleSendButtonClick = () => {
      onSend()
    }

    const showPdfAttachmentPreview =
      pendingAttachmentMime === 'application/pdf' ||
      (pendingImagePreview?.startsWith('data:application/pdf') ?? false)
    const showPendingAttachment = hasPendingImage && !imageUploadAcknowledged
    const treatAsVideo =
      Boolean(pendingAttachmentMime?.startsWith('video/')) ||
      Boolean(pendingImagePreview?.startsWith('data:video/'))
    const treatAsSpreadsheet =
      pendingAttachmentMime === 'text/csv' ||
      pendingAttachmentMime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    const emphasizeAttachment =
      Boolean(allowDocuments && highlightProjectAutofillAttachment && !hasPendingImage)
    const showHistory =
      typeof onOpenHistory === 'function' &&
      typeof historyCount === 'number' &&
      historyCount > 0
    const historyLabel = historyCount && historyCount > 99 ? '99+' : String(historyCount ?? '')
    const fileAccept = getComposerFileAccept(allowVideo, allowDocuments)
    const uploadIconMediaOnly = composerUsesMediaOnlyIcon(allowVideo, allowDocuments)
    const uploadLabelClass = [styles.imageUploadBtn, emphasizeAttachment ? styles.imageUploadBtnEmphasis : '']
      .filter(Boolean)
      .join(' ')

    const attachLabel =
      !hasPendingImage && showImageUpload ? (
        <label
          htmlFor={fileInputId}
          className={uploadLabelClass}
          title={
            allowVideo ? 'Agregar imagen o video' : allowDocuments ? 'Agregar archivo' : 'Agregar imagen'
          }
          aria-label={
            allowVideo ? 'Agregar imagen o video' : allowDocuments ? 'Agregar archivo' : 'Agregar imagen'
          }
        >
          {uploadIconMediaOnly ? (
            <ImagePlus size={20} strokeWidth={1.8} />
          ) : (
            <Paperclip size={20} strokeWidth={1.85} />
          )}
        </label>
      ) : null

    const pendingDocCompare = useMemo(() => {
      const pending = pendingAttachmentFileName?.trim()
      const last = lastAnalyzedDocumentationFileName?.trim()
      if (!pending || !last) return null
      return normalizeFileNameForCompare(pending) === normalizeFileNameForCompare(last)
        ? ('same' as const)
        : ('different' as const)
    }, [pendingAttachmentFileName, lastAnalyzedDocumentationFileName])

    const pendingSizeLabel =
      typeof pendingAttachmentSizeBytes === 'number' && pendingAttachmentSizeBytes > 0
        ? formatCompactFileSize(pendingAttachmentSizeBytes)
        : ''

    const resolvedPlaceholder =
      showPendingAttachment && postMediaIntentChoicePending
        ? 'Elegí una opción arriba. Podés sumar instrucciones opcionales…'
        : showPendingAttachment && documentIntentChoicePending
          ? 'Elegí una opción arriba. Podés sumar instrucciones opcionales…'
          : showPendingAttachment &&
              showProjectAutofillSendHint &&
              variant === 'composer' &&
              !documentIntentChoicePending
            ? 'Aclaración opcional para el análisis…'
            : showPendingAttachment &&
                showPostMediaSendHint &&
                variant === 'composer' &&
                !postMediaIntentChoicePending
              ? 'Texto opcional…'
              : showPendingAttachment && allowDocuments
                ? 'Instrucciones o aclaraciones para el análisis (opcional)…'
                : showPendingAttachment
                  ? 'Opcional: mensaje junto al archivo…'
                  : placeholder

    const showDocumentIntent =
      Boolean(documentIntentChoicePending && onConfirmDocumentAsProjectPreload && onImageClear)

    const showPostMediaIntent = Boolean(
      postMediaIntentChoicePending && onConfirmPostMediaRoute && onImageClear
    )

    const showPortfolioRoutesInPostGate = !treatAsVideo

    const pendingFileSummaryRow = (
      <div className={styles.pendingAttachmentRow}>
        <div className={styles.pendingThumbWrap}>
          <div
            className={styles.pendingImagePreview}
            aria-label={
              showPdfAttachmentPreview
                ? 'PDF listo para enviar'
                : treatAsSpreadsheet
                  ? 'Archivo listo para enviar'
                  : treatAsVideo
                    ? 'Video listo para enviar'
                    : 'Archivo listo para enviar'
            }
          >
            {treatAsVideo && pendingImagePreview ? (
              <video src={pendingImagePreview} controls />
            ) : showPdfAttachmentPreview || treatAsSpreadsheet ? (
              <div className={styles.pendingPdfIcon}>
                <FileText size={20} strokeWidth={1.85} aria-hidden />
              </div>
            ) : pendingImagePreview ? (
              <img src={pendingImagePreview} alt="Vista previa" />
            ) : (
              <div className={styles.pendingPdfIcon}>
                <FileText size={20} strokeWidth={1.85} aria-hidden />
              </div>
            )}
          </div>
          <button
            type="button"
            className={styles.clearImageBtn}
            onClick={onImageClear}
            aria-label="Quitar archivo"
          >
            <X size={12} />
          </button>
        </div>
        <div className={styles.pendingAttachmentMeta}>
          {pendingAttachmentFileName ? (
            <span className={styles.pendingFileName} title={pendingAttachmentFileName}>
              {pendingAttachmentFileName}
            </span>
          ) : (
            <span className={styles.pendingFileNameMuted}>Archivo listo para enviar</span>
          )}
          {pendingSizeLabel ? <span className={styles.pendingFileSize}>{pendingSizeLabel}</span> : null}
          {allowDocuments && pendingDocCompare === 'same' ? (
            <span className={styles.pendingDocCompareSame}>
              Mismo nombre que la última documentación analizada en esta charla
            </span>
          ) : null}
          {allowDocuments && pendingDocCompare === 'different' ? (
            <span className={styles.pendingDocCompareDiff}>
              Distinto del último archivo analizado en esta charla
            </span>
          ) : null}
        </div>
      </div>
    )

    const pendingAttachmentBlock = showPendingAttachment ? (
      <div
        className={`${styles.pendingAttachmentBand} ${
          showDocumentIntent || showPostMediaIntent ? styles.pendingAttachmentBandIntegrated : ''
        }`}
      >
        {showDocumentIntent ? (
          <div className={styles.documentIntentIntegrated}>
            <div className={styles.documentIntentLeftRail}>
              <p className={styles.documentIntentQuestionIntegrated}>
                ¿Querés cotizar una obra con este archivo?
              </p>
              <div className={styles.documentIntentFileLinked}>{pendingFileSummaryRow}</div>
            </div>
            <div className={styles.documentIntentActionsIntegrated}>
              <button
                type="button"
                className={styles.documentIntentBtnPrimary}
                onClick={() => void onConfirmDocumentAsProjectPreload()}
              >
                Sí, cotizar obra
              </button>
              <button type="button" className={styles.documentIntentBtnGhost} onClick={onImageClear}>
                No, quitar
              </button>
            </div>
          </div>
        ) : showPostMediaIntent ? (
          <div className={styles.documentIntentIntegrated}>
            <div className={styles.documentIntentLeftRail}>
              <p className={styles.documentIntentQuestionIntegrated}>
                ¿Qué querés hacer con {treatAsVideo ? 'este video' : 'esta imagen o video'}?
              </p>
              <div className={styles.documentIntentFileLinked}>{pendingFileSummaryRow}</div>
            </div>
            <div className={styles.documentIntentActionsIntegrated}>
              <button
                type="button"
                className={styles.documentIntentBtnPrimary}
                onClick={() => void onConfirmPostMediaRoute?.('post')}
              >
                Posteo
              </button>
              {showPortfolioRoutesInPostGate ? (
                <>
                  <button
                    type="button"
                    className={styles.documentIntentBtnPrimary}
                    onClick={() => void onConfirmPostMediaRoute?.('producto')}
                  >
                    Producto
                  </button>
                  <button
                    type="button"
                    className={styles.documentIntentBtnPrimary}
                    onClick={() => void onConfirmPostMediaRoute?.('servicio')}
                  >
                    Servicio
                  </button>
                </>
              ) : null}
              <button type="button" className={styles.documentIntentBtnGhost} onClick={onImageClear}>
                No, quitar
              </button>
            </div>
          </div>
        ) : (
          pendingFileSummaryRow
        )}
      </div>
    ) : null

    const composerControls = (
      <>
        {showHistory && (
          <button
            type="button"
            className={styles.historyButton}
            onClick={onOpenHistory}
            aria-label="Ver conversaciones anteriores"
            title="Historial de conversaciones"
          >
            <MessageSquare size={18} />
            <span className={styles.historyBadge} aria-hidden="true">
              {historyLabel}
            </span>
          </button>
        )}
        {showDraftPublish && onPublishDraft ? (
          <button
            type="button"
            className={draftPublishStyles.draftPublishButton}
            onClick={onPublishDraft}
            disabled={disabled || draftPublishing}
            aria-label="Publicar obra"
          >
            {draftPublishing ? (
              <span>Publicando...</span>
            ) : (
              <>
                <Rocket size={18} strokeWidth={1.8} aria-hidden />
                <span>Publicar obra</span>
              </>
            )}
          </button>
        ) : null}
        {readyToExecute && onConfirmExecution ? (
          <button
            className={styles.confirmButton}
            onClick={onConfirmExecution}
            disabled={disabled}
            type="button"
            aria-label="Confirmar creación"
          >
            <Check size={18} />
            <span>Confirmar</span>
          </button>
        ) : variant === 'welcome' ? (
          <>
            {(value.trim() || hasPendingImage) && onWelcomeReset ? (
              <button
                type="button"
                className={styles.welcomeDraftClear}
                onClick={onWelcomeReset}
                disabled={disabled}
                aria-label="Limpiar"
              >
                <X size={16} />
              </button>
            ) : null}
            {attachLabel}
            <button
              type="submit"
              className={welcomeSendClassName || styles.sendButton}
              disabled={disabled || !canSend}
              aria-label="Enviar mensaje"
            >
              <SendHorizonal size={18} />
            </button>
          </>
        ) : (
          <button
            className={styles.sendButton}
            onClick={handleSendButtonClick}
            disabled={disabled || !canSend}
            type="button"
            aria-label="Enviar mensaje"
          >
            <SendHorizonal size={18} />
          </button>
        )}
      </>
    )

    const wrapperClass = [
      styles.inputWrapper,
      variant === 'welcome' ? styles.welcomeComposerFill : ''
    ]
      .filter(Boolean)
      .join(' ')
    const containerClass = [
      showPendingAttachment
        ? `${styles.inputContainer} ${styles.inputContainerStacked}`
        : styles.inputContainer,
      variant === 'welcome' ? styles.inputContainerWelcomeEmbed : ''
    ]
      .filter(Boolean)
      .join(' ')

    const welcomeTextareaClass = welcomeTextareaClassName?.trim() || ''
    const welcomeUseBottomChrome = variant === 'welcome' && !showPendingAttachment
    const welcomeTextareaChromeClass = welcomeUseBottomChrome ? styles.inputWelcomeAbsoluteChrome : ''

    const welcomeActionsBarClass = welcomeActionsClassName?.trim() || styles.welcomeActionsBottomRight

    return (
      <div className={wrapperClass}>
        <div
          className={containerClass}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {showImageUpload && (
            <>
              <input
                ref={fileInputRef}
                id={fileInputId}
                type="file"
                accept={fileAccept}
                className={styles.fileInputHidden}
                onChange={handleImageChange}
                disabled={disabled}
                aria-hidden
                tabIndex={-1}
              />
              {showPendingAttachment
                ? pendingAttachmentBlock
                : welcomeUseBottomChrome
                  ? null
                  : attachLabel}
            </>
          )}
          {showPendingAttachment &&
          showProjectAutofillSendHint &&
          variant === 'composer' &&
          !documentIntentChoicePending ? (
            <p className={styles.projectAutofillSendHint} role="status">
              Cuando quieras, enviá el mensaje para iniciar el análisis del PDF. Podés sumar una aclaración
              opcional en el texto.
            </p>
          ) : null}
          {showPendingAttachment &&
          showPostMediaSendHint &&
          variant === 'composer' &&
          !postMediaIntentChoicePending ? (
            <p className={styles.projectAutofillSendHint} role="status">
              Cuando quieras, enviá el mensaje para seguir. Podés sumar texto opcional abajo.
            </p>
          ) : null}
          {showPendingAttachment ? (
            <div
              className={
                variant === 'welcome'
                  ? `${styles.composerRow} ${styles.composerRowWelcomeStacked}`
                  : styles.composerRow
              }
            >
              <textarea
                ref={textareaRef}
                className={
                  variant === 'welcome' && welcomeTextareaClass
                    ? `${styles.inputWelcomeEmbed} ${styles.inputWelcomeStackedChrome} ${styles.inputWithPendingAttachment} ${welcomeTextareaClass}`
                    : `${styles.input} ${styles.inputWithPendingAttachment}`
                }
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={onKeyPress}
                onPaste={handlePaste}
                onFocus={onInputFocus}
                onBlur={onInputBlur}
                placeholder={resolvedPlaceholder}
                disabled={disabled}
                rows={2}
              />
              <div className={welcomeActionsBarClass}>{composerControls}</div>
            </div>
          ) : welcomeUseBottomChrome ? (
            <>
              <textarea
                ref={textareaRef}
                className={
                  welcomeTextareaClass
                    ? `${styles.inputWelcomeEmbed} ${welcomeTextareaChromeClass} ${welcomeTextareaClass}`
                    : `${styles.inputWelcomeEmbed} ${welcomeTextareaChromeClass}`
                }
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={onKeyPress}
                onPaste={handlePaste}
                onFocus={onInputFocus}
                onBlur={onInputBlur}
                placeholder={placeholder}
                disabled={disabled}
                rows={1}
              />
              <div className={welcomeActionsBarClass}>{composerControls}</div>
            </>
          ) : (
            <>
              <textarea
                ref={textareaRef}
                className={styles.input}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={onKeyPress}
                onPaste={handlePaste}
                onFocus={onInputFocus}
                onBlur={onInputBlur}
                placeholder={placeholder}
                disabled={disabled}
                rows={1}
              />
              {composerControls}
            </>
          )}
        </div>
        {emphasizeAttachment ? (
          <p className={styles.attachmentHint} role="status">
            Adjuntá un archivo (PDF, imagen, CSV o Excel) para crear el borrador de obra.
          </p>
        ) : null}
      </div>
    )
  }
)

ConversationInput.displayName = 'ConversationInput'

export default ConversationInput
